import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  nativeCsvReader,
  type NativeCsvReaderResult,
} from 'sprites-types/workflow/tools/nativeCsvReader'
import { DataTable } from '../shared/DataTable'
import { MarkdownResult } from '../shared/MarkdownResult'
import { SchemaValidationError } from '../shared/safeParseToolResult'

interface Props {
  nodeExecution: NodeExecution
}

export function NativeCsvReaderDisplay({ nodeExecution }: Props) {
  const schema = getToolResultSchema(nativeCsvReader, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data

  // Handle emitMode and nullable behavior
  const results: NativeCsvReaderResult[] = Array.isArray(parsed)
    ? parsed.filter((item): item is NativeCsvReaderResult => Boolean(item))
    : parsed
      ? [parsed as NativeCsvReaderResult]
      : []

  // Process the CSV data from the results
  const allRows = results.flatMap((result) => {
    if (!result.results || result.results.length === 0) {
      return []
    }

    // Convert CsvDataRow format to table format
    return result.results
      .filter(
        (row): row is { id: string; hs: string[]; row: string[] } => 'row' in row && 'hs' in row
      ) // Only include rows with data, not filtered/deleted ones
      .map((row) => {
        const headers = row.hs
        const values = row.row
        return headers.reduce(
          (obj: Record<string, string>, header: string, index: number) => {
            obj[header] = values[index] || ''
            return obj
          },
          {} as Record<string, string>
        )
      })
  })

  if (allRows.length > 0) {
    return <DataTable data={allRows} title={`CSV Data (${allRows.length} rows)`} maxRows={50} />
  }

  // Fallback to markdown display
  return <MarkdownResult data={results} title="CSV Reader Results" />
}
